import { Mail01 } from '@untitledui/icons';
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { sanitizeError } from '@/lib/utils/errors';

import FormErrors from '../../components/FormErrors.tsx';
import AuthLayout from '../../layouts/AuthLayout.tsx';
import { requestLoginCode } from '../../lib/allauth.js';
import type { AllauthResponse } from '../../types/allauth';

export default function RequestLoginCode() {
  const [email, setEmail] = useState('');
  const [response, setResponse] = useState<{
    fetching: boolean;
    content: AllauthResponse | null;
  }>({ fetching: false, content: null });

  function submit() {
    setResponse({ ...response, fetching: true });
    requestLoginCode(email)
      .then(content => {
        setResponse(r => {
          return { ...r, content };
        });
      })
      .catch(e => {
        console.error(e);
        toast.error(sanitizeError(e));
      })
      .then(() => {
        setResponse(r => {
          return { ...r, fetching: false };
        });
      });
  }

  if (response.content?.status === 401) {
    return <Navigate to='/account/login/code/confirm' />;
  }
  return (
    <AuthLayout title='Email sign-in code'>
      <div className='flex flex-col gap-8'>
        {/* Header */}
        <div className='flex flex-col gap-3'>
          <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
            Email sign-in code
          </h1>
          <p className='text-base text-gray-600 dark:text-gray-400'>
            We'll send you a secure sign-in code to access your account without
            a password.
          </p>
        </div>

        {/* Form */}
        <div className='flex flex-col gap-6'>
          <FormErrors errors={response.content?.errors} />

          <div>
            <label
              className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5'
              htmlFor='email'
            >
              Email
            </label>
            <Input
              type='email'
              id='email'
              placeholder='Enter your email'
              isRequired
              onChange={value => setEmail(value)}
              value={email}
              name='email'
            />
            <FormErrors param='email' errors={response.content?.errors} />
          </div>

          <div className='flex flex-col gap-4'>
            <Button
              onClick={() => submit()}
              disabled={response.fetching}
              className='w-full'
              isLoading={response.fetching}
              showTextWhileLoading
            >
              {response.fetching ? (
                'Sending code…'
              ) : (
                <div className='flex items-center justify-center gap-2'>
                  <Mail01 size={16} />
                  Send sign-in code
                </div>
              )}
            </Button>

            <Link to='/account/login' className='w-full'>
              <Button color='secondary' className='w-full'>
                Back to password sign in
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
